import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { dropdownArrow } from '../../assets/icons';
import { ParticipantListView } from '../../lib/Types';

import {
  ParticipantDropdownContainer,
  ParticipantDropdownHeader,
  ParticipantDropdownLabel,
  ParticipantDropdownArrow,
  ParticipantDropdownOptions,
  ParticipantDropdownOption,
  ParticipantDropdownSearch,
  ParticipantDropdownEmpty
} from './Styles';

const formatParticipantLabel = (participant: ParticipantListView): string =>
  `${participant.email} (${participant.fullName})`;

interface ParticipantEmailDropdownProps {
  participants: ParticipantListView[];
  value: string | null;
  disabled?: boolean;
  placeholder?: string;
  onChange: (userId: string | null) => void;
}

const ParticipantEmailDropdown: FC<ParticipantEmailDropdownProps> = ({
  participants,
  value,
  disabled = false,
  placeholder = 'Select a participant',
  onChange
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedParticipant = useMemo(
    () => participants.find(participant => participant.participantId === value),
    [participants, value]
  );

  const filteredParticipants = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return participants;
    return participants.filter(participant => participant.email.toLowerCase().includes(term));
  }, [participants, searchTerm]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open && !disabled) {
      searchRef.current?.focus();
    }
  }, [open, disabled]);

  const handleToggle = () => {
    if (disabled) return;
    setOpen(prev => {
      if (prev) setSearchTerm('');
      return !prev;
    });
  };

  const handleSelect = (userId: string | null) => {
    onChange(userId);
    setOpen(false);
    setSearchTerm('');
  };

  const displayLabel = selectedParticipant
    ? formatParticipantLabel(selectedParticipant)
    : placeholder;

  return (
    <ParticipantDropdownContainer ref={containerRef} $disabled={disabled} $open={open}>
      <ParticipantDropdownHeader onClick={handleToggle}>
        {open && !disabled ? (
          <ParticipantDropdownSearch
            ref={searchRef}
            type="text"
            value={searchTerm}
            placeholder="Search by email..."
            onClick={e => e.stopPropagation()}
            onChange={e => setSearchTerm(e.target.value)}
          />
        ) : (
          <ParticipantDropdownLabel $disabled={disabled}>{displayLabel}</ParticipantDropdownLabel>
        )}
        <ParticipantDropdownArrow alt="dropdown arrow" src={dropdownArrow} />
      </ParticipantDropdownHeader>
      {open && !disabled ? (
        <ParticipantDropdownOptions>
          <ParticipantDropdownOption $bold onClick={() => handleSelect(null)}>
            Show All
          </ParticipantDropdownOption>
          {filteredParticipants.length ? (
            filteredParticipants.map(participant => (
              <ParticipantDropdownOption
                key={participant.participantId}
                onClick={() => handleSelect(participant.participantId)}
              >
                {formatParticipantLabel(participant)}
              </ParticipantDropdownOption>
            ))
          ) : (
            <ParticipantDropdownEmpty>No matching emails</ParticipantDropdownEmpty>
          )}
        </ParticipantDropdownOptions>
      ) : null}
    </ParticipantDropdownContainer>
  );
};

export default ParticipantEmailDropdown;
